import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Lock, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { createCheckout, money, validatePromo } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Reveal from "@/components/Reveal";

const COUNTRIES = ["US", "CA", "GB", "DE", "FR", "AU", "IN", "AE", "SG"];

export default function Checkout() {
  const { items, subtotal, promo, setPromo, discount, shipping, total, paymentsMode } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", country: "US" });
  const [promoInput, setPromoInput] = useState("");
  const [placing, setPlacing] = useState(false);

  // Mark checkout as "in progress" so leaving it later can trigger the one-time offer
  useEffect(() => {
    if (items.length) localStorage.setItem("voltaic_abandoned", "1");
  }, [items.length]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    try {
      const res = await validatePromo(promoInput, subtotal, form.email);
      if (res.valid) {
        setPromo({ code: res.code, percent: res.percent, kind: res.kind });
        toast.success(
          res.kind === "referral_reward"
            ? `${res.code} redeemed — your 10% referral credit`
            : `${res.code} applied — ${res.percent}% off`
        );
      } else {
        setPromo(null);
        toast.error("Invalid promo code");
      }
    } catch {
      toast.error("Could not validate promo");
    }
  };

  const pay = async () => {
    const required = ["name", "email", "address", "city", "zip"];
    if (required.some((k) => !form[k].trim())) {
      toast.error("Fill in your shipping details first");
      return;
    }
    setPlacing(true);
    localStorage.removeItem("voltaic_abandoned");
    try {
      const data = await createCheckout({
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: form,
        promo_code: promo?.code || "",
        origin_url: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed — try again");
      setPlacing(false);
    }
  };

  if (!items.length) {
    return (
      <main className="mx-auto flex max-w-xl flex-col items-center gap-5 px-5 pb-24 pt-40 text-center" data-testid="checkout-empty-state">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">Checkout</p>
        <h1 className="font-display text-3xl font-extrabold uppercase">Your cart is empty</h1>
        <p className="text-voltaic-muted">Add a device or two before hitting the grid.</p>
        <Link to="/shop" data-testid="checkout-empty-shop-link" className="mt-2 flex items-center gap-2 rounded-full bg-voltaic-cyan px-7 py-3.5 font-semibold text-voltaic-bg hover:glow-cyan">
          <ArrowLeft size={16} /> Back to the catalogue
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 lg:px-10" data-testid="checkout-page">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">Secure checkout // {paymentsMode === "live" ? "Live payments" : "Stripe test mode"}</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">Ship it. Fire it up.</h1>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Shipping form */}
        <Reveal className="lg:col-span-7" delay={0.05}>
          <div className="rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6 sm:p-8">
            <div className="mb-6 flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-voltaic-cyan font-mono text-xs font-bold text-voltaic-bg">1</span>
              <h2 className="font-display text-lg font-bold uppercase tracking-wide">Shipping details</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">Full name</label>
                <input data-testid="checkout-name-input" className="field" value={form.name} onChange={set("name")} placeholder="Ada Lovelace" />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">Email</label>
                <input data-testid="checkout-email-input" type="email" className="field" value={form.email} onChange={set("email")} placeholder="ada@voltaic.dev" />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">Phone (optional)</label>
                <input data-testid="checkout-phone-input" className="field" value={form.phone} onChange={set("phone")} placeholder="+1 555 010 2030" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">Address</label>
                <input data-testid="checkout-address-input" className="field" value={form.address} onChange={set("address")} placeholder="101 Circuit Avenue" />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">City</label>
                <input data-testid="checkout-city-input" className="field" value={form.city} onChange={set("city")} placeholder="Neon City" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">State</label>
                  <input data-testid="checkout-state-input" className="field" value={form.state} onChange={set("state")} placeholder="CA" />
                </div>
                <div>
                  <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">ZIP</label>
                  <input data-testid="checkout-zip-input" className="field" value={form.zip} onChange={set("zip")} placeholder="90210" />
                </div>
                <div>
                  <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">Country</label>
                  <select data-testid="checkout-country-select" className="field" value={form.country} onChange={set("country")}>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-voltaic-cyan/30 bg-voltaic-cyan/5 p-4" data-testid="checkout-test-mode-note">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-voltaic-cyan" />
              <p className="text-xs leading-relaxed text-voltaic-muted">
                {paymentsMode === "live" ? (
                  <>
                    <span className="font-semibold text-voltaic-green">Live payments on.</span> You'll pay on Stripe's secure
                    page with a real card — your card details never touch our servers.
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-voltaic-cyan">Stripe test mode.</span> You'll pay on Stripe's secure page with the test card{" "}
                    <span className="font-mono text-voltaic-ink">4242 4242 4242 4242</span> — any future expiry, any CVC. No real money moves.
                  </>
                )}
              </p>
            </div>
          </div>
        </Reveal>

        {/* Order summary */}
        <Reveal className="lg:col-span-5" delay={0.1}>
          <div className="rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6 sm:p-8 lg:sticky lg:top-28">
            <div className="mb-6 flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-voltaic-cyan font-mono text-xs font-bold text-voltaic-bg">2</span>
              <h2 className="font-display text-lg font-bold uppercase tracking-wide">Order summary</h2>
            </div>

            <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.product_id} data-testid={`checkout-item-${i.product_id}`} className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <img src={i.image} alt={i.name} className="h-14 w-14 rounded-xl object-cover" />
                    <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-voltaic-cyan px-1 text-[10px] font-bold text-voltaic-bg">
                      {i.quantity}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{i.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-voltaic-muted">{money(i.price)} each</p>
                  </div>
                  <p className="text-sm font-bold">{money(i.price * i.quantity)}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 flex gap-2">
              <input
                data-testid="checkout-promo-input"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                placeholder="Promo or referral code"
                className="field flex-1 !py-2.5 text-sm"
              />
              <button
                data-testid="checkout-promo-apply"
                onClick={applyPromo}
                className="rounded-xl border border-voltaic-linebright px-4 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
              >
                Apply
              </button>
            </div>
            <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-voltaic-muted">
              <Users size={11} className="text-voltaic-cyan" /> Friend's code VOLT-XXXXXX = 10% off · VOLTAIC20 = 20% off
            </p>

            <div className="mt-5 space-y-1.5 text-sm">
              <div className="flex justify-between text-voltaic-muted"><span>Subtotal</span><span data-testid="checkout-subtotal">{money(subtotal)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-voltaic-green"><span>Promo {promo.code}</span><span data-testid="checkout-discount">-{money(discount)}</span></div>
              )}
              <div className="flex justify-between text-voltaic-muted"><span>Shipping</span><span data-testid="checkout-shipping">{shipping === 0 ? "Free" : money(shipping)}</span></div>
              <div className="flex justify-between text-voltaic-muted"><span>Tax</span><span className="font-mono text-[10px] uppercase">At Stripe checkout</span></div>
              <div className="flex justify-between border-t border-voltaic-line pt-3 font-display text-xl font-extrabold"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
            </div>

            <button
              data-testid="pay-now-button"
              onClick={pay}
              disabled={placing}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-voltaic-cyan py-4 font-display font-bold uppercase tracking-wide text-voltaic-bg transition-shadow duration-300 hover:glow-cyan disabled:cursor-wait disabled:opacity-60"
            >
              {placing ? (
                <span className="flex items-center gap-2"><span className="h-4 w-4 animate-spin rounded-full border-2 border-voltaic-bg/40 border-t-voltaic-bg" /> Opening Stripe...</span>
              ) : (
                <>
                  <Lock size={16} /> Pay {money(total)} securely
                </>
              )}
            </button>
            <p className="mt-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">
              <CreditCard size={13} /> Visa · Mastercard · Amex{paymentsMode === "live" ? "" : " — test cards only"}
            </p>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
